/**
 * Constructor.io facet mapping — maps CIO facet names to human-readable display
 * names, and doubles as the allowlist of facets the storefront will filter by.
 *
 * Source of truth: SFCC Business Manager site preference
 * `Constructor_FacetMapping` (per site), read at request time by
 * `~/hec/.server/facet-mapping.server` and threaded through the PLP/search
 * loaders into `parseHecFilters` (server) and `CioFacetProvider` (client).
 * Format: { "Display Name": "cio_facet_name" }
 *
 * The mapping controls:
 * 1. Which facets appear in the filter drawer (only mapped facets show)
 * 2. What display name each facet gets (left side of the mapping)
 * 3. Which URL params are accepted as CIO filters (allowlist — SFCC-11336)
 *
 * This module is deliberately PURE + isomorphic: it holds the mapping shape,
 * the derivation of the lookup structures, and the fallback map. It performs no
 * preference I/O, so it can be imported from client bundles.
 *
 * `FALLBACK_FACET_MAPPING` is the last-known-good BM value, used only when the
 * site preference cannot be read or is unusable — never merged with it (a
 * merge would resurrect facets a merchandiser deliberately removed in BM).
 */

/**
 * Canonical CIO facet key for LMN eligibility.
 *
 * DO NOT "fix" the spelling. The extra `i` in `Eligibile` is canonical in the
 * production Constructor.io index and in SFCC Business Manager
 * (`Constructor_SendToConstructor`, `Constructor_FacetMapping`). Correcting it
 * here without a coordinated SFCC + Constructor.io rename will silently break
 * facet filtering on PLP/Search.
 *
 * Tracked under SFCC-10607.
 */
export const LMN_ELIGIBILITY_FACET_KEY = "LMN-Eligibile";

/** BM site preference holding the display-name → CIO-facet-name mapping. */
export const FACET_MAPPING_PREFERENCE_ID = "Constructor_FacetMapping";

/**
 * Last-known-good `Constructor_FacetMapping` (identical across FSA/HSA/WDH as
 * of release 2026.04). Used ONLY as the fallback when the live preference is
 * unreadable or unusable, so a Preferences API blip degrades to the previous
 * behaviour instead of emptying the filter drawer and dropping every `?facet=`
 * filter. Refresh it when BM's value changes materially — it is a safety net,
 * not the source of truth.
 */
export const FALLBACK_FACET_MAPPING: Record<string, string> = {
  category: "classificationCategory",
  "product type": "productType",
  brand: "brand",
  price: "price",
  count: "count",
  spf: "spf",
  absorbency: "absorbency",
  power: "power",
  features: "character",
  color: "color",
  "item size": "itemSize",
  form: "form",
  product: "product",
  hazmat: "hazmat",
  is_default: "is_default",
  eligibility: LMN_ELIGIBILITY_FACET_KEY,
  sale: "sale",
  "women owned brand": "gender",
  "average rating": "averageRating",
  // Parity with SFRA `Constructor_FacetMapping` (all 3 sites, release 2026.04).
  // Required since SFCC-11336 made this an allowlist — without it a
  // `?replenishable=` filter is silently dropped instead of reaching CIO.
  replenishable: "replenishable",
  // Added to the CIO feed in dev (2026-08). Both are returned by CIO with
  // populated options on real PLPs (e.g. FSA `/shop-by-price`), but were
  // invisible in the filter drawer because this map is an allowlist — an
  // unmapped facet is dropped by `filterAndRenameCioFacets` no matter how it's
  // configured in the Constructor dashboard.
  "health condition": "healthCondition",
  specials: "specials",
};

/**
 * Everything the filter surfaces need, derived once per distinct mapping:
 * the raw BM mapping (display → CIO name, serialisable to the client), the
 * inverted lookup used to rename CIO facets for display, and the allowlist of
 * CIO facet names.
 */
export type FacetMappingConfig = {
  /** BM `Constructor_FacetMapping` shape, verbatim: display name → CIO name. */
  mapping: Record<string, string>;
  /** Inverted mapping: CIO facet name → display name (O(1) drawer lookups). */
  displayByCioName: Record<string, string>;
  /**
   * CIO facet names that may be displayed AND the canonical URL-param
   * allowlist. Consumed by `parseHecFilters` (server, builds CIO filters) and
   * `cio-facet-adapter` (client, derives/clears selected facets) so only real
   * facets reach Constructor — non-facet params (AFID, utm_*, gclid, src) are
   * dropped (SFCC-11336). Pruning it also changes which URL params survive
   * "clear all" and reach CIO, not just what the drawer renders.
   */
  allowed: Set<string>;
};

/** Derive the lookup structures for one display-name → CIO-name mapping. */
export function createFacetMappingConfig(
  mapping: Record<string, string>,
): FacetMappingConfig {
  return {
    mapping,
    displayByCioName: Object.fromEntries(
      Object.entries(mapping).map(([display, cio]) => [cio, display]),
    ),
    allowed: new Set(Object.values(mapping)),
  };
}

/** Derived fallback config — the behaviour before the preference is resolved. */
export const FALLBACK_FACET_MAPPING_CONFIG: FacetMappingConfig =
  createFacetMappingConfig(FALLBACK_FACET_MAPPING);

/**
 * Upper bound on mapped facets. A pref this large is a BM data error (the real
 * value has ~22 entries); accepting it unbounded would let one bad paste inflate
 * the allowlist and every PLP payload. Over the cap → treated as unusable.
 */
const MAX_MAPPED_FACETS = 200;

/**
 * Parse a raw `Constructor_FacetMapping` preference value into a mapping.
 *
 * Returns `null` when the value is missing, not JSON, not a flat object, or has
 * no usable entries — callers fall back to `FALLBACK_FACET_MAPPING`. Individual
 * non-string / blank entries are dropped rather than failing the whole map, so
 * one malformed line in BM cannot take down every filter. Values are trimmed
 * (BM text fields collect stray whitespace) but otherwise passed through
 * verbatim: CIO facet names are case-sensitive, including the canonical
 * `LMN-Eligibile` misspelling.
 */
export function parseFacetMappingPref(
  raw: unknown,
): Record<string, string> | null {
  if (typeof raw !== "string" || raw.trim() === "") return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (
    parsed === null ||
    typeof parsed !== "object" ||
    Array.isArray(parsed) ||
    Object.keys(parsed).length > MAX_MAPPED_FACETS
  ) {
    return null;
  }

  const mapping: Record<string, string> = {};
  for (const [display, cioName] of Object.entries(
    parsed as Record<string, unknown>,
  )) {
    if (typeof cioName !== "string") continue;
    const displayName = display.trim();
    const facetName = cioName.trim();
    if (!displayName || !facetName) continue;
    mapping[displayName] = facetName;
  }

  return Object.keys(mapping).length > 0 ? mapping : null;
}

/**
 * Legacy URL-param aliases for CIO facet names. Mirrors SFRA's
 * `LegacyURLParamMapping` (org_hec `config/dict.json`) verbatim: prod's React
 * Constructor cartridge emits/accepts the kebab-case `product-type` URL param
 * for the CIO facet `productType`. Ported so Workers PLP URLs stay
 * byte-identical to prod (canonical / SEO-indexed / shared / marketing-link
 * parity) AND inbound legacy `?product-type=` URLs still apply the filter
 * instead of landing on an unfiltered PLP. Keyed CIO facet name → legacy URL
 * param. A single entry today (the only entry in SFRA's mapping), kept
 * data-driven in case more legacy params surface. (SFCC-11638)
 *
 * Static on purpose: SFRA holds this in a cartridge config file, not a site
 * preference, so there is nothing in BM to read it from. It is orthogonal to
 * `Constructor_FacetMapping` — that pref decides WHICH facets exist, this
 * decides how one of them is spelled in the URL.
 */
export const LEGACY_URL_PARAM_MAPPING: Record<string, string> = {
  productType: "product-type",
};

// Reverse lookup: legacy URL param → CIO facet name.
// ponytail: assumes alias values (`product-type`) are disjoint from CIO facet
// names. True for the single entry today; if the map grows an alias that equals
// another facet's CIO name, this reverse lookup mis-resolves — add a collision
// check then.
const URL_PARAM_TO_CIO_FACET: Record<string, string> = Object.fromEntries(
  Object.entries(LEGACY_URL_PARAM_MAPPING).map(([cio, param]) => [param, cio]),
);

/**
 * CIO facet name → the URL param to EMIT: the legacy alias when one exists
 * (`productType` → `product-type`), else the name unchanged. Apply at every
 * URL-write boundary (facet adapter `SET_CIO_SELECTED_FACETS`, curated-pill
 * `applyFilter`, canonical builder) so emitted links match prod. (SFCC-11638)
 */
export function cioFacetToUrlParam(cioName: string): string {
  return LEGACY_URL_PARAM_MAPPING[cioName] ?? cioName;
}

/**
 * URL param → CIO facet name: reverse the legacy alias (`product-type` →
 * `productType`), else the param unchanged. Apply at every URL-read boundary
 * (`parseHecFilters`, facet adapter selected-state) so BOTH the legacy
 * `product-type` and the raw `productType` resolve to the one CIO facet — the
 * CIO request and internal state always key by the CIO name. (SFCC-11638)
 */
export function urlParamToCioFacet(param: string): string {
  return URL_PARAM_TO_CIO_FACET[param] ?? param;
}

/**
 * Is this URL param a CIO facet filter we manage? True iff it's a facet in the
 * given (BM-resolved) mapping AND not `price`: `price` is in the allowlist for
 * display/rename, but is driven solely by `pmin`/`pmax` — a raw `?price=` is
 * never accepted as a filter. Shared by the server filter builder
 * (`parseHecFilters`) and the client facet adapter (selected-facet read +
 * clear-all) so the read and clear sides stay in exact lockstep (SFCC-11336).
 * `pmin`/`pmax`/`page` are handled explicitly by callers, not here. Accepts
 * legacy URL aliases too (`product-type` resolves to `productType`) so inbound
 * prod-format URLs filter and are cleared like the raw CIO name (SFCC-11638).
 *
 * `config` is required: every call site must state WHICH mapping it is gating
 * against, so a facet surface can never silently drift back onto the baked-in
 * fallback while the rest of the page uses BM's live value.
 */
export function isCioFacetParam(
  key: string,
  config: FacetMappingConfig,
): boolean {
  const cioName = urlParamToCioFacet(key);
  return cioName !== "price" && config.allowed.has(cioName);
}
